// ─── Scoped-role access control ───────────────────────────────────────────────
// Confines a *restricted* role to a fixed set of route prefixes on both the sidebar
// and the router, WITHOUT affecting any normal role. A user is "scoped" only when their
// permissions map to a known restricted profile below, or when the login belongs to a partner;
// every existing staff role (which holds broad permissions, or '*') returns null here and is
// therefore never restricted.
//
// Detection is permission-derived for staff (not role-name based): the OLT operator's permissions
// live entirely within the {olt, tr069} modules, which no existing role does — so only that role
// matches. Partner logins are recognised from `partnerId`, which the API puts on the profile.

export interface ScopedProfile {
  /** Route prefixes this role may visit (e.g. ['/olt']). */
  allowedPrefixes: string[];
  /** Prefixes refused even though an allowed prefix covers them. */
  deniedPrefixes?: string[];
  /** Where to land after login / when redirected off a disallowed page. */
  home: string;
  /** Human label for the scope (used in the Forbidden/redirect UX). */
  label: string;
}

/** Ambient "shell" modules that every user may hold and that do NOT count toward scoping. */
const SHELL_MODULES = new Set(['auth', 'preferences', 'notifications']);

/**
 * A partner's own login, or staff using Login as Partner.
 *
 * The API is what actually confines them (PartnerScopeGuard refuses every endpoint not written for
 * partners); this only keeps menus and pages to the ones that work. `/subscribers` is their own
 * subscriber list and a subscriber's page — the dashboard, onboarding and static-IP queue under the
 * same prefix read tenant-wide data and are refused.
 */
const PARTNER_PROFILE: ScopedProfile = {
  allowedPrefixes: ['/partner-portal', '/subscribers'],
  deniedPrefixes: ['/subscribers/new', '/subscribers/dashboard', '/subscribers/static-ip-requests'],
  home: '/partner-portal',
  label: 'Partner portal',
};

/**
 * Restricted profiles, in match order.
 *
 * A profile matches when EVERY non-shell module the user holds is in its `modules` set, so a role
 * that also carries an unrelated permission (dashboard, subscribers, …) deliberately fails to match
 * and stays unrestricted — narrowing a broad role by accident would be far worse than not scoping.
 *
 * Note the failure direction: an unmatched permission set returns null, which means UNRESTRICTED.
 * A new restricted role therefore needs an entry here; granting it narrow permissions alone would
 * leave it seeing the whole portal, because the sidebar has no permission-level gating of its own.
 */
const SCOPED_PROFILES: Array<{ modules: Set<string>; profile: ScopedProfile }> = [
  {
    // OLT operator — limited to OLT + TR-069 management only.
    modules: new Set(['olt', 'tr069']),
    profile: { allowedPrefixes: ['/olt'], home: '/olt/overview', label: 'OLT Management' },
  },
  {
    // IPDR / CGNAT compliance officer — lawful-intercept section only. `compliance` rides along
    // because the IPDR module claims both permission prefixes (see module-registry).
    modules: new Set(['ipdr', 'compliance']),
    profile: { allowedPrefixes: ['/ipdr'], home: '/ipdr', label: 'IPDR / Compliance' },
  },
  {
    // Store roles (Inventory Admin, Store / Warehouse Manager, Store Operator) hold inventory permissions only —
    // everything they need about jobs and subscribers reaches them through the inventory API.
    modules: new Set(['inventory']),
    profile: { allowedPrefixes: ['/inventory', '/settings/inventory'], home: '/inventory', label: 'Inventory' },
  },
];

/**
 * Map a user to a restricted profile, or null if the user is unrestricted.
 * A partner login is always scoped, whatever its permissions say.
 */
export function getScopedProfile(
  permissions: string[] | undefined | null,
  partnerId?: string | null,
): ScopedProfile | null {
  if (partnerId) return PARTNER_PROFILE;
  if (!permissions || permissions.length === 0) return null;
  if (permissions.includes('*')) return null; // super-admin: never scoped
  const modules = new Set(
    permissions.map((p) => p.split('.')[0]).filter((m) => !SHELL_MODULES.has(m)),
  );
  if (modules.size === 0) return null;
  const hit = SCOPED_PROFILES.find((s) => [...modules].every((m) => s.modules.has(m)));
  return hit ? hit.profile : null;
}

/** True if `path` falls under one of the profile's allowed prefixes and none of its denied ones (boundary-safe). */
export function isPathAllowed(path: string, profile: ScopedProfile): boolean {
  const under = (pre: string) => path === pre || path.startsWith(pre + '/');
  if (profile.deniedPrefixes?.some(under)) return false;
  return profile.allowedPrefixes.some(under);
}
